import { LegalPageLayout } from "@/components/legal/LegalPageLayout";
import { Link } from "react-router-dom";
import { usePageMeta } from "@/hooks/usePageMeta";

const CGU = () => {
  usePageMeta({
    title: "Conditions Générales d'Utilisation — B8ild",
    description: "Consultez les conditions générales d'utilisation de B8ild, logiciel de gestion de chantier BTP.",
    url: "https://b8ild.fr/cgu",
  });

  return (
    <LegalPageLayout
      title="Conditions Générales d'Utilisation"
      subtitle="Dernière mise à jour : Janvier 2026"
    >
      <h2>Article 1 - Objet</h2>
      <p>
        Les présentes Conditions Générales d'Utilisation (ci-après "CGU") ont pour objet de définir
        les modalités et conditions d'utilisation de la plateforme B8ild accessible à l'adresse{" "}
        <a href="https://b8ild.fr" target="_blank" rel="noopener noreferrer">https://b8ild.fr</a>{" "}
        (ci-après "la Plateforme"), ainsi que les droits et obligations des parties dans ce cadre.
      </p>
      <p>
        B8ild est une plateforme SaaS de gestion de rentabilité de chantiers destinée aux
        professionnels du BTP.
      </p>

      <h2>Article 2 - Acceptation des CGU</h2>
      <p>
        L'utilisation de la Plateforme implique l'acceptation pleine et entière des présentes CGU.
        En créant un compte, l'Utilisateur reconnaît avoir lu, compris et accepté l'intégralité
        des présentes conditions.
      </p>
      <p>
        B8ild se réserve le droit de modifier les présentes CGU à tout moment. Les modifications
        entrent en vigueur dès leur publication sur la Plateforme.
      </p>

      <h2>Article 3 - Accès à la Plateforme</h2>
      <h3>3.1.</h3>
      <p>
        L'accès à la Plateforme nécessite une connexion internet et un navigateur web compatible.
      </p>
      <h3>3.2.</h3>
      <p>
        B8ild s'efforce d'assurer l'accessibilité de la Plateforme 24h/24 et 7j/7, mais ne peut
        garantir une disponibilité permanente. Des interruptions pour maintenance peuvent survenir.
      </p>

      <h2>Article 4 - Inscription et Compte Utilisateur</h2>
      <h3>4.1.</h3>
      <p>
        Pour accéder aux services, l'Utilisateur doit créer un compte en fournissant des informations
        exactes et complètes (nom, email, informations de l'entreprise).
      </p>
      <h3>4.2.</h3>
      <p>
        L'Utilisateur est seul responsable de la confidentialité de ses identifiants de connexion
        (email et mot de passe).
      </p>
      <h3>4.3.</h3>
      <p>
        L'Utilisateur s'engage à informer immédiatement B8ild de toute utilisation non autorisée
        de son compte via{" "}
        <a href="mailto:contact@b8ild.fr">contact@b8ild.fr</a>.
      </p>
      <h3>4.4.</h3>
      <p>
        Un compte est strictement personnel et ne peut être cédé ou partagé sans autorisation.
      </p>

      <h2>Article 5 - Description des Services</h2>
      <p>B8ild propose les services suivants :</p>
      <ul>
        <li>Suivi de la rentabilité des chantiers en temps réel</li>
        <li>Calcul automatique des marges et du "Jour Critique"</li>
        <li>Gestion des équipes et affectations</li>
        <li>Import et traitement des factures par OCR (reconnaissance optique)</li>
        <li>Analyse financière par intelligence artificielle</li>
        <li>Génération de rapports et bilans de chantier</li>
        <li>Alertes et notifications</li>
      </ul>
      <p>
        Les fonctionnalités disponibles dépendent du plan d'abonnement souscrit (Starter, Pro, Entreprise).
      </p>

      <h2>Article 6 - Abonnements et Tarifs</h2>
      <h3>6.1.</h3>
      <p>
        B8ild propose plusieurs formules d'abonnement dont les tarifs sont indiqués sur la page Tarifs
        du site (<Link to="/pricing">https://b8ild.fr/pricing</Link>).
      </p>
      <h3>6.2.</h3>
      <p>
        Les prix sont indiqués en euros hors taxes (HT). La TVA applicable sera ajoutée au moment du paiement.
      </p>
      <h3>6.3.</h3>
      <p>
        La facturation peut être mensuelle ou annuelle selon le choix de l'Utilisateur. L'abonnement
        annuel bénéficie d'une réduction.
      </p>
      <h3>6.4.</h3>
      <p>
        Un essai gratuit de 14 jours est proposé. Une carte bancaire est requise pour commencer l'essai
        mais aucun prélèvement n'est effectué avant la fin de la période d'essai.
      </p>

      <h2>Article 7 - Modalités de Paiement</h2>
      <h3>7.1.</h3>
      <p>
        Le paiement s'effectue par carte bancaire via notre prestataire de paiement sécurisé Stripe.
      </p>
      <h3>7.2.</h3>
      <p>
        Les données bancaires sont traitées directement par Stripe et ne sont jamais stockées sur nos serveurs.
      </p>
      <h3>7.3.</h3>
      <p>
        En cas d'échec de paiement, l'accès aux services peut être suspendu après notification par email.
      </p>

      <h2>Article 8 - Résiliation</h2>
      <h3>8.1.</h3>
      <p>
        L'Utilisateur peut résilier son abonnement à tout moment depuis son espace personnel ou en
        contactant <a href="mailto:contact@b8ild.fr">contact@b8ild.fr</a>.
      </p>
      <h3>8.2.</h3>
      <p>
        La résiliation prend effet à la fin de la période de facturation en cours.
      </p>
      <h3>8.3.</h3>
      <p>
        Après résiliation, l'Utilisateur conserve l'accès à ses données pendant 30 jours pour les exporter.
        Passé ce délai, les données seront supprimées.
      </p>
      <h3>8.4.</h3>
      <p>
        B8ild se réserve le droit de suspendre ou résilier l'accès de tout Utilisateur en cas de violation
        des présentes CGU, sans préavis ni indemnité.
      </p>

      <h2>Article 9 - Obligations de l'Utilisateur</h2>
      <p>L'Utilisateur s'engage à :</p>
      <ul>
        <li>Utiliser la Plateforme conformément à sa destination professionnelle</li>
        <li>Fournir des informations exactes et à jour</li>
        <li>Ne pas tenter de contourner les mesures de sécurité</li>
        <li>Ne pas utiliser la Plateforme à des fins illégales ou frauduleuses</li>
        <li>Ne pas perturber le fonctionnement de la Plateforme</li>
        <li>Respecter les droits de propriété intellectuelle de B8ild</li>
      </ul>

      <h2>Article 10 - Propriété Intellectuelle</h2>
      <h3>10.1.</h3>
      <p>
        L'ensemble des éléments de la Plateforme (textes, images, logos, logiciels, base de données, interface)
        sont la propriété exclusive de B8ild et sont protégés par les lois relatives à la propriété intellectuelle.
      </p>
      <h3>10.2.</h3>
      <p>
        Toute reproduction, représentation ou exploitation non autorisée est strictement interdite.
      </p>
      <h3>10.3.</h3>
      <p>
        L'Utilisateur conserve l'entière propriété de ses données (informations chantiers, factures, données
        financières). B8ild n'acquiert aucun droit sur ces données.
      </p>

      <h2>Article 11 - Données Personnelles</h2>
      <p>
        Le traitement des données personnelles est décrit dans notre{" "}
        <Link to="/confidentialite">Politique de Confidentialité</Link>.
      </p>

      <h2>Article 12 - Responsabilité</h2>
      <h3>12.1.</h3>
      <p>B8ild s'engage à fournir un service de qualité mais ne peut garantir :</p>
      <ul>
        <li>Un fonctionnement ininterrompu de la Plateforme</li>
        <li>L'absence totale d'erreurs ou de bugs</li>
        <li>La compatibilité avec tous les équipements</li>
      </ul>
      <h3>12.2.</h3>
      <p>B8ild ne saurait être tenu responsable :</p>
      <ul>
        <li>Des dommages indirects (perte de chiffre d'affaires, perte de données)</li>
        <li>Des décisions prises par l'Utilisateur sur la base des informations fournies par la Plateforme</li>
        <li>Des interruptions dues à des cas de force majeure</li>
      </ul>
      <h3>12.3.</h3>
      <p>
        Les analyses et calculs fournis par B8ild sont des outils d'aide à la décision. L'Utilisateur reste
        seul responsable de ses décisions de gestion.
      </p>
      <h3>12.4.</h3>
      <p>
        La responsabilité de B8ild est limitée au montant des sommes effectivement versées par l'Utilisateur
        au cours des 12 derniers mois.
      </p>

      <h2>Article 13 - Force Majeure</h2>
      <p>
        B8ild ne pourra être tenue responsable en cas de force majeure, notamment : pannes, interruptions
        de réseaux, catastrophes naturelles, actes de terrorisme, grèves, ou tout événement indépendant de sa volonté.
      </p>

      <h2>Article 14 - Loi Applicable et Litiges</h2>
      <h3>14.1.</h3>
      <p>Les présentes CGU sont soumises au droit français.</p>
      <h3>14.2.</h3>
      <p>
        En cas de litige, les parties s'engagent à rechercher une solution amiable avant toute action judiciaire.
      </p>
      <h3>14.3.</h3>
      <p>
        À défaut d'accord amiable dans un délai de 30 jours, les tribunaux français seront seuls compétents.
      </p>

      <h2>Article 15 - Contact</h2>
      <p>Pour toute question relative aux présentes CGU :</p>
      <ul>
        <li>Email : <a href="mailto:contact@b8ild.fr">contact@b8ild.fr</a></li>
        <li>Page contact : <Link to="/contact">https://b8ild.fr/contact</Link></li>
      </ul>
    </LegalPageLayout>
  );
};

export default CGU;
